// MCP connector functions for the canvas workspace project registry.
//
// Registered via canvas/functions/index.js so MCPConnectorPickerOverlay can
// call them via el.call('saveMcpConnector', draft) and
// el.call('reloadMcpConnectors').
//
// DOMQL v3.14 call convention: method.call(element, ...args)
//   — `this` inside each function IS the calling element.
//   — Access root state via this.getRoot() (same as getRoot() in v3.14).
//   — No el.props.X — flat access only.

// ── Workspace ID helper ───────────────────────────────────────────────────────

// Derive the active workspace ID from the governance Supabase session's
// app_metadata claims. Returns null when not authenticated or claims absent.
async function _getWorkspaceId() {
  try {
    const mod = await import('../../shared/governanceBridge.js')
    const client = mod.getClient?.()
    if (!client?.auth?.getSession) return null
    const { data } = await client.auth.getSession()
    return data?.session?.user?.app_metadata?.active_workspace_id || null
  } catch {
    return null
  }
}

// ── saveMcpConnector ──────────────────────────────────────────────────────────

/**
 * Install a new MCP connector.
 *
 * Primary path: sdk-bridge.installConnector (→ POST /core/mcp-connectors).
 * Fallback path: direct governance Supabase insert when the server endpoint
 * is not yet deployed, so local dev and early QA are unblocked.
 *
 * `this` is the calling element (DOMQL v3.14 call convention).
 *
 * @param {object} draft — form draft from MCPConnectorPickerOverlay:
 *   { url, name, transport, authKind, authToken, scopeType, scopeId }
 * @returns {Promise<void>} — throws on unrecoverable error.
 */
export async function saveMcpConnector(draft) {
  if (!draft?.url) throw new Error('URL required')

  const workspaceId = await _getWorkspaceId()
  if (!workspaceId) throw new Error('Not authenticated — no active workspace')

  const scopeType = draft.scopeType || 'workspace'
  const scopeId = draft.scopeId || null
  const name = draft.name || draft.url
  const transport = draft.transport || 'sse'
  const authKind = draft.authKind || 'none'
  const authToken = draft.authToken

  // installConnector lives in shared/mcp; the Supabase fallback insert
  // below goes through governanceBridge.getClient. Pull both in parallel.
  const [mcpMod, bridgeMod] = await Promise.all([
    import('../../shared/mcp/index.js'),
    import('../../shared/governanceBridge.js')
  ])
  const mod = { ...bridgeMod, ...mcpMod }

  // Primary path: server-side install (Vault secret + capability discovery).
  if (typeof mod.installConnector === 'function') {
    const res = await mod.installConnector({
      workspaceId,
      scopeType,
      scopeId,
      name,
      url: draft.url,
      transport,
      authKind,
      authToken
    })

    if (res?.ok) return

    // Propagate hard auth errors immediately.
    if (res?.error && String(res.error).includes('not authenticated')) {
      throw new Error(res.error)
    }

    // 404 / "Cannot POST" → server route not yet deployed. Fall through to
    // direct Supabase insert so QA is unblocked.
    const looksLikeNotFound =
      String(res?.error || '').includes('404') ||
      String(res?.error || '').includes('Cannot POST') ||
      String(res?.error || '').includes('not found') ||
      String(res?.error || '').includes('network error')

    if (!looksLikeNotFound) {
      throw new Error(res?.error || 'installConnector failed')
    }
    // Fall through to direct insert below.
  }

  // Fallback: direct Supabase insert (no Vault / no capability discovery).
  // Works for local dev and early QA where /core/mcp-connectors isn't deployed.
  //
  // TODO[sdk-mcp-route]: wire `getSDK().execute('mcp.connectors', 'create', ...)`
  // once a dedicated MCP entity route is added to EntityDispatcher.js. The
  // sdk-bridge `installConnector` path above already routes through
  // /core/mcp-connectors — once that endpoint is fully deployed across
  // every environment, the fallback can be removed entirely.
  const client = mod.getClient?.()
  if (!client) throw new Error('sdk-bridge governance client not configured')

  const { error } = await client.from('mcp_connectors').insert({
    workspace_id: workspaceId,
    scope_type: scopeType,
    scope_id: scopeId,
    name,
    url: draft.url,
    transport,
    auth_kind: authKind,
    status: 'enabled',
    capabilities: {},
    metadata: {}
  })

  if (error) throw new Error(error.message)
}

// ── reloadMcpConnectors ───────────────────────────────────────────────────────

/**
 * Re-fetch MCP connectors and update root._mcpConnectors.
 *
 * Uses sdk-bridge.listConnectors (direct Supabase SELECT, RLS-filtered by
 * jwt_active_workspace()) and writes to root state so MCPConnectorRail and
 * MCPConnectorChips re-render reactively.
 *
 * `this` is the calling element (DOMQL v3.14 call convention). Root is reached
 * via this.getRoot() which walks up to the page root — the same contract as
 * MCPConnectorPickerOverlay's s.root.update() calls.
 *
 * @returns {Promise<void>}
 */
export async function reloadMcpConnectors() {
  try {
    const mod = await import('../../shared/mcp/index.js')
    if (typeof mod.listConnectors !== 'function') return

    const { data, error } = await mod.listConnectors()
    if (error) {
      console.warn('[mcp] reloadMcpConnectors error:', error)
      return
    }

    // `this` is the element that called el.call('reloadMcpConnectors').
    // Walk to root and update _mcpConnectors.
    const root = typeof this?.getRoot === 'function' ? this.getRoot() : null
    if (root?.update) {
      root.update({ _mcpConnectors: data || [] })
    } else {
      console.warn('[mcp] reloadMcpConnectors: could not resolve root element')
    }
  } catch (err) {
    console.warn('[mcp] reloadMcpConnectors threw:', err?.message || err)
  }
}
